import { createServerFn } from "@tanstack/react-start";
import { authMiddleware } from "#/modules/util.auth/middleware.auth";
import { Result, type InferOk } from "better-result";
import { getDefaultDateRange } from "#/modules/klassen.insights/filters.klassen.insights";
import type { KlasBeweegactiviteitenFilters } from "#/modules/klassen.insights/schema.search";
import { getClassesForStartLesson } from "#/modules/school.klassen/api.klassen.server";
import { toResult } from "#/modules/util.auth/api-errors";
import { client } from "#/modules/util.fetch-client/fetch-client.server";
import type { components } from "#/schema";

type FilterModel = components["schemas"]["FilterTreeModel"];

/** Klas element as returned by `getClassesForStartLesson` (`/school/klassen`). */
type KlasItem = InferOk<Awaited<ReturnType<typeof getClassesForStartLesson>>>["data"][number];

export interface InsightsActivitiesData {
  klassen: KlasItem[];
  leerlijnFilter: FilterModel | null;
  beweegactiviteitProgressItems: components["schemas"]["KlasBeweegactiviteitProgressModel"][];
  hasSelectedActivities: boolean;
  /** Server-resolved effective class (explicit selection, else first class). `null` = no classes. */
  selectedKlasId: string | null;
}

interface GetInsightsActivitiesInput {
  filters: KlasBeweegactiviteitenFilters;
}

export const getInsightsActivities = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator((data: GetInsightsActivitiesInput) => data)
  .handler(async ({ data }): Promise<InsightsActivitiesData> => {
    const { filters } = data;
    const defaultDates = getDefaultDateRange();

    const klassenResult = await getClassesForStartLesson();
    if (Result.isError(klassenResult)) throw klassenResult.error;
    const klassen = klassenResult.value.data ?? [];

    // Resolve the effective class server-side: an explicit selection wins, otherwise default to the
    // first class. Previously this branch `throw redirect`ed to add `?klasId=<first>` to the URL,
    // which is not cacheable during preload and caused a preload redirect-chase storm. We now resolve
    // the default here and return it as `selectedKlasId`; the URL only changes on explicit selection.
    const effectiveKlasId = filters.klasId ?? (klassen[0] ? klassen[0].id.toString() : undefined);

    let beweegactiviteitProgressItems: InsightsActivitiesData["beweegactiviteitProgressItems"] = [];
    let leerlijnFilter: FilterModel | null = null;

    if (effectiveKlasId) {
      const progressResult = toResult(
        await client.GET("/school/klassen/beweegactiviteiten-progress", {
          params: {
            query: {
              klasId: effectiveKlasId,
              from: filters.from || defaultDates.from,
              to: filters.to || defaultDates.to,
              "filterSelection.selectedLeerlijnFilterIds": filters.selectedLeerlijnFilterIds || [],
              includeWithProgress: filters.includeWithProgress ?? true,
              includeWithPartialProgress: filters.includeWithPartialProgress ?? true,
              includeWithoutProgress: filters.includeWithoutProgress ?? true,
              includeWithDefaults: filters.includeWithDefaults ?? true,
              includeWithCustom: filters.includeWithCustom ?? true,
            },
          },
        }),
      );

      if (Result.isError(progressResult)) {
        throw progressResult.error;
      }

      beweegactiviteitProgressItems = progressResult.value?.beweegactiviteitProgressItems || [];
      leerlijnFilter = progressResult.value?.leerlijnFilter ?? null;
    }

    // Check if any selected activities exist in the current results
    const selectedActivitiesForKlas = effectiveKlasId
      ? filters.selectedBeweegactiviteitenByKlas?.[effectiveKlasId] || []
      : [];
    const availableActivityIds = new Set(
      beweegactiviteitProgressItems.map((item) => item.beweegactiviteitId),
    );
    const hasSelectedActivities = selectedActivitiesForKlas.some((id) =>
      availableActivityIds.has(id),
    );

    return {
      klassen,
      beweegactiviteitProgressItems,
      leerlijnFilter,
      hasSelectedActivities,
      selectedKlasId: effectiveKlasId ?? null,
    };
  });

interface GetInsightsActivityDetailInput {
  klasId?: string | null;
  beweegactiviteitId: string;
  from?: string | null;
  to?: string | null;
}

export const getInsightsActivityDetail = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator((data: GetInsightsActivityDetailInput) => data)
  .handler(async ({ data }) => {
    const { klasId, beweegactiviteitId, from, to } = data;
    const defaultDates = getDefaultDateRange();

    if (!klasId || !beweegactiviteitId) {
      throw new Error("Missing required parameters");
    }

    const result = toResult(
      await client.GET("/school/klassen/beweegactiviteiten-progress/detail", {
        params: {
          query: {
            klasId,
            beweegactiviteitId,
            from: from || defaultDates.from,
            to: to || defaultDates.to,
          },
        },
      }),
    );

    if (Result.isError(result)) {
      throw result.error;
    }

    return {
      detail: result.value,
      beweegactiviteitId,
    };
  });
